import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db702o1gdmoohm3rngd0";

const fieldClass =
  "w-full bg-background-50 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition";
const labelClass = "block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wider";

type Status = "idle" | "submitting" | "success" | "error";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function LeaseForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = ((data.get("website_alt") as string | null) ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value as string);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const okCode = parsed?.code === "OK";

      if (!response.ok || !okCode || isSpam) {
        setStatus("error");
        setFormError(serverMsg || t("contact.form.errorGeneric"));
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError(t("contact.form.errorNetwork"));
    }
  };

  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-7 md:p-10">
      <h3 className="font-heading text-2xl font-bold text-foreground-900">{t("contact.form.title")}</h3>
      <p className="mt-2 text-foreground-500 text-sm">{t("contact.form.subtitle")}</p>

      <form
        data-readdy-form="true"
        id="arman-lease-inquiry-form"
        onSubmit={handleSubmit}
        className="mt-6 space-y-4"
      >
        <div className="hp-guard-field" aria-hidden="true">
          <label>
            Do not fill this field
            <input
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              name="website_alt"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className={labelClass}>
              {t("contact.form.name")}
            </label>
            <input
              id="name"
              required
              className={fieldClass}
              placeholder={t("contact.form.namePlaceholder")}
              name="name"
            />
          </div>
          <div>
            <label htmlFor="phone" className={labelClass}>
              {t("contact.form.phone")}
            </label>
            <input
              id="phone"
              required
              className={fieldClass}
              placeholder="+374 ..."
              type="tel"
              name="phone"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className={labelClass}>
            {t("contact.form.email")}
          </label>
          <input
            id="email"
            className={fieldClass}
            placeholder="you@example.com"
            type="email"
            name="email"
          />
        </div>

        <div>
          <label htmlFor="vehicle" className={labelClass}>
            {t("contact.form.vehicleLabel")}
          </label>
          <select id="vehicle" name="vehicle" required className={fieldClass} defaultValue="">
            <option value="" disabled>
              {t("contact.form.vehiclePlaceholder")}
            </option>
            <option value="Bike Lease">{t("contact.form.optionBike")}</option>
            <option value="Scooter Lease">{t("contact.form.optionScooter")}</option>
            <option value="Car Lease">{t("contact.form.optionCar")}</option>
            <option value="Service / Repair">{t("contact.form.optionService")}</option>
            <option value="Spare Parts">{t("contact.form.optionParts")}</option>
            <option value="Hero Splendor (New)">{t("contact.form.optionHero")}</option>
          </select>
        </div>

        <div>
          <label htmlFor="message" className={labelClass}>
            {t("contact.form.message")}
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            className={`${fieldClass} resize-none`}
            placeholder={t("contact.form.messagePlaceholder")}
          />
          <div className="text-xs text-foreground-400 mt-1">{t("contact.form.maxChars")}</div>
        </div>

        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 font-semibold px-6 py-4 rounded-md whitespace-nowrap cursor-pointer transition"
        >
          <i className="ri-send-plane-fill w-5 h-5 flex items-center justify-center" />
          {status === "submitting" ? t("contact.form.sending") : t("contact.form.submit")}
        </button>

        {status === "success" && (
          <p className="flex items-start gap-2 text-sm text-accent-800 bg-accent-50 border border-accent-300 rounded-md px-4 py-3">
            <i className="ri-checkbox-circle-fill w-5 h-5 flex items-center justify-center shrink-0" />
            {t("contact.form.success")}
          </p>
        )}

        {status === "error" && (
          <p className="flex items-start gap-2 text-sm text-primary-800 bg-primary-50 border border-primary-300 rounded-md px-4 py-3">
            <i className="ri-error-warning-fill w-5 h-5 flex items-center justify-center shrink-0" />
            {formError}
          </p>
        )}
      </form>
    </div>
  );
}